import './PositionListItem.scss';
import React from 'react';
import clsx from 'clsx';
import { MdEmojiEvents } from 'react-icons/md';
import Tooltip from '@srm/ui/tooltip';
import { IPosition } from '@models/winnerSelection/ITablePositions';
import { PosInfo, getStatus, resolveWinnerNames } from '../utils/posInfoMerge';
import { PositionDescription } from './PositionDescription';
import { renderWinnersTooltipContent } from './winnersTooltipContent';

interface IProps {
    position: IPosition;
    info: PosInfo | undefined;
    isActive: boolean;
    /** Y-смещение от virtualizer'а. */
    top: number;
    /** Индекс в окне виртуализации, проставляется в data-index — virtualizer
     *  использует data-index в measureElement для отслеживания строки. */
    dataIndex: number;
    onSelect: (p: IPosition) => void;
    /** Колбэк-ref от virtualizer.measureElement — измеряет реальную высоту
     *  карточки (ResizeObserver внутри). Без него высота карточки фиксирована
     *  estimateSize, и при переносе winner-имени контент вылезает за пределы. */
    measureRef?: (node: HTMLDivElement | null) => void;
}

/**
 * Одна карточка позиции в списке AsideList. Вынесена из inline-map ради React.memo:
 * при смене currentKey ре-рендерится только пара бывшая-активная/новая-активная,
 * а не все ~10 видимых карточек.
 *
 * shallow-compare пропсов корректен: position/info — ссылочно стабильны между
 * ре-рендерами parent'а до реального изменения данных; onSelect — setCurrentPosition
 * из useState, тоже стабильна.
 */
const PositionListItemInner: React.FC<IProps> = ({
    position: p,
    info,
    isActive,
    top,
    dataIndex,
    onSelect,
    measureRef,
}) => {
    const st = getStatus(p, info);
    const winnerNames = p.winnerNames && p.winnerNames.length > 0 ? p.winnerNames : resolveWinnerNames(info);
    const firstWinner = winnerNames[0];
    const extraWinners = winnerNames.length - 1;
    const offersCount = p.offersCount ?? info?.offersCount;

    return (
        <div
            ref={measureRef}
            data-index={dataIndex}
            className={clsx('ws-pos-item', `ws-pos-item--${st}`, { 'is-active': isActive })}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                transform: `translateY(${top}px)`,
            }}
            onClick={() => onSelect(p)}
            role="button"
            /* Roving tabindex: tab-фокус только на активной. */
            tabIndex={isActive ? 0 : -1}
            aria-current={isActive ? 'page' : undefined}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(p);
                }
            }}
        >
            <div className="ws-pos-item__mark" />
            <div className="ws-pos-item__body">
                <div className="ws-pos-item__name-row">
                    <div className="ws-pos-item__name" title={p.name}>
                        {p.name}
                    </div>
                    <PositionDescription text={p.description} />
                </div>
                <div className="ws-pos-item__sub">
                    {(p.branch || p.subdivision) && (
                        <span className="branch">
                            {p.branch}
                            {p.branch && p.subdivision ? ' · ' : ''}
                            {p.subdivision}
                        </span>
                    )}
                    {offersCount != null && <span>{offersCount} предл.</span>}
                    {firstWinner && (
                        <Tooltip
                            background="black"
                            wrapTarget={false}
                            interactive={false}
                            openDelay={250}
                            disabled={!(winnerNames.length > 1)}
                            position="top"
                            label={renderWinnersTooltipContent(winnerNames)}
                        >
                            <span className="winner">
                                <MdEmojiEvents size="11px" />
                                <span className="winner__name">{firstWinner}</span>
                                {extraWinners > 0 && (
                                    <span className="winner__more" aria-label={`Ещё ${extraWinners} победителей`}>
                                        +{extraWinners}
                                    </span>
                                )}
                            </span>
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};

export const PositionListItem = React.memo(PositionListItemInner);
